import { User } from '@sentry/react';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from '../lib/logging';
import { toLoggedUrl } from '../lib/logging/LLog.middleware';
import { sentry } from '../sentry/sentry.singleton.ts';
import { readStoredRecord, readStoredString } from '../utils/storage.ts';
import {
  BASE_URL_BFF_SERVICE,
  BASE_URL_BILLING_SERVICE,
  BASE_URL_LEGACY,
} from './axios.constants';
import { axiosInstance } from './axios.singleton';
import { tAxiosCustomConfig } from './axios.types.ts';

/*
 * This function creates the request configuration
 * @param {tAxiosCustomConfig} config - The configuration for the request
 * @returns {AxiosRequestConfig} - The request configuration
 * BUG-801: auth headers (Authorization / X-Api-Key) are attached by the
 * request interceptor in axios.singleton.ts from the tab's live Firebase
 * session — not here from localStorage. Custom headers passed per-request
 * (e.g. an invitation token) survive and take precedence in the interceptor.
 * depending on the isLegacy flag, the baseURL is set to the legacy base URL
 */
const createRequestConfig = (
  config?: tAxiosCustomConfig,
): AxiosRequestConfig => {
  const { headers: customHeaders, ...restConfig } = config || {};
  const requestConfig: AxiosRequestConfig = {
    ...restConfig,
    headers: {
      ...customHeaders,
    },
  };

  if (config?.isLegacy) {
    requestConfig.baseURL = BASE_URL_LEGACY;
  }

  if (config?.isBillingService) {
    requestConfig.baseURL = BASE_URL_BILLING_SERVICE;
  }

  if (config?.isBFFService) {
    requestConfig.baseURL = BASE_URL_BFF_SERVICE;
  }

  return requestConfig;
};

function readUserField(
  currentUser: Record<string, unknown> | undefined,
  field: string,
): string {
  const value = currentUser?.[field];
  return typeof value === 'string' ? value : '';
}

const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * INC-4: every failed request already carried its endpoint, but only in `extra` —
 * which Sentry does not index. Searching `generateFunnel` returned zero issues
 * while a correctly-grouped issue for it sat open and unread since 2026-05-20;
 * the only thing that found it was the literal string "timeout of 30000ms
 * exceeded", buried among 174 results. That blindness affects every endpoint,
 * not just this one, so the tag goes on at the shared call sites.
 *
 * The raw pathname must NOT be tagged: it embeds a workspace UUID, so tagging it
 * verbatim creates an unbounded-cardinality tag (one value per workspace per
 * route, ~2,700 and growing) that Sentry degrades on and may silently drop.
 * Normalising to the route pattern bounds cardinality by route count instead.
 * The raw path stays in `extra` for triage.
 */
export function normalizeRoute(pathname: string): string {
  return pathname
    .split('/')
    .map((segment) => {
      if (UUID_SEGMENT.test(segment)) return ':id';
      if (/^\d+$/.test(segment)) return ':n';
      return segment;
    })
    .join('/');
}

/*
 * BUG-801: every verb calls this from its catch block, so a corrupt Web Storage
 * entry parsed here would replace the real API error with an uncaught throw out
 * of the error handler. The storage readers degrade to undefined instead, and
 * the Sentry user context falls back to empty strings.
 */
function getUserInfo(): User {
  const currentUser = readStoredRecord('currentUser');

  return {
    id: readUserField(currentUser, 'authId'),
    email: readUserField(currentUser, 'email'),
    username: readUserField(currentUser, 'name'),
    phoneNumber: readUserField(currentUser, 'phoneNumber'),
    workspaceId: readStoredString('currentWorkspaceId') || '',
    funnelId: readStoredString('currentFunnelID') || '',
  };
}

async function patchWithResponse<tRequest, tResponse>(
  pathname: string,
  request?: tRequest,
  config?: tAxiosCustomConfig,
): Promise<AxiosResponse<tResponse>> {
  try {
    LLog.info(
      { component: LOG_COMPONENTS.HTTP, method: 'PATCH', operation: LOG_OPERATIONS.API_CALL },
      `Calling PATCH ${pathname}`,
      { request: request as unknown, config: config as unknown },
    );
    const requestConfig = createRequestConfig(config);
    const response = await axiosInstance.patch<tResponse, AxiosResponse<tResponse>, tRequest>(pathname, request, requestConfig);
    LLog.info(
      { component: LOG_COMPONENTS.HTTP, method: 'PATCH', operation: LOG_OPERATIONS.RESPONSE },
      `PATCH ${pathname} success`,
      { status: response.status, data: response.data },
    );
    return response;
  } catch (error) {
    LLog.error(
      { component: LOG_COMPONENTS.HTTP, method: 'PATCH', operation: LOG_OPERATIONS.ERROR },
      `PATCH ${pathname} failed`,
      error as Error,
      { request: request as unknown, config: config as unknown },
    );
    if (!config?.skipSentryCapture) {
      sentry.captureException(error, {
        user: getUserInfo(),
        tags: { endpoint: normalizeRoute(pathname) },
        extra: { endpoint: pathname, axiosConfig: config, request },
      });
    }
    return Promise.reject(error);
  }
}

/**
 * Whether a failed call goes to Sentry. A call opts out entirely with
 * `skipSentryCapture`, or only for the statuses that are answers of its flow
 * with `skipSentryCaptureForStatuses` (BUG-800: the 428 with which sign-in and
 * sign-up refuse an invited address, which every wrong-door attempt meets).
 */
function shouldCaptureFailure(
  error: unknown,
  config?: tAxiosCustomConfig,
): boolean {
  if (config?.skipSentryCapture) return false;
  const status = (error as { response?: { status?: number } } | undefined)
    ?.response?.status;
  return (
    status === undefined ||
    !config?.skipSentryCaptureForStatuses?.includes(status)
  );
}

export const AxiosService = Object.freeze({
  post: async function <tRequest, tResponse>(
    pathname: string,
    request?: tRequest,
    config?: tAxiosCustomConfig,
  ): Promise<tResponse> {
    try {
      LLog.info(
        {
          component: LOG_COMPONENTS.HTTP,
          method: 'POST',
          operation: LOG_OPERATIONS.API_CALL,
        },
        `Calling POST ${pathname}`,
        { request: request as unknown, config: config as unknown },
      );
      const requestConfig: tAxiosCustomConfig = createRequestConfig(config);
      const response = await axiosInstance.post(
        pathname,
        request,
        requestConfig,
      );
      LLog.info(
        {
          component: LOG_COMPONENTS.HTTP,
          method: 'POST',
          operation: LOG_OPERATIONS.RESPONSE,
        },
        `POST ${pathname} success`,
        { status: response.status, data: response.data },
      );
      return Promise.resolve(response.data);
    } catch (error) {
      LLog.error(
        {
          component: LOG_COMPONENTS.HTTP,
          method: 'POST',
          operation: LOG_OPERATIONS.ERROR,
        },
        `POST ${pathname} failed`,
        error as Error,
        { request: request as unknown, config: config as unknown },
      );
      if (shouldCaptureFailure(error, config)) {
        sentry.captureException(error, {
          user: getUserInfo(),
          tags: { endpoint: normalizeRoute(pathname) },
          extra: {
            endpoint: pathname,
            axiosConfig: config,
            request: request,
          },
        });
      }
      return Promise.reject(error);
    }
  },

  get: async function <T>(
    pathname: string,
    config?: tAxiosCustomConfig,
  ): Promise<T> {
    // The email check carries the address a person typed in its path.
    const loggedPath = toLoggedUrl(pathname, config?.redactLog);
    try {
      LLog.info(
        {
          component: LOG_COMPONENTS.HTTP,
          method: 'GET',
          operation: LOG_OPERATIONS.API_CALL,
        },
        `Calling GET ${loggedPath}`,
        { config: config as unknown },
      );
      const requestConfig: tAxiosCustomConfig = createRequestConfig(config);
      const response = await axiosInstance.get(pathname, requestConfig);
      LLog.info(
        {
          component: LOG_COMPONENTS.HTTP,
          method: 'GET',
          operation: LOG_OPERATIONS.RESPONSE,
        },
        `GET ${loggedPath} success`,
        { status: response.status, data: response.data },
      );
      return Promise.resolve(response.data);
    } catch (error) {
      LLog.error(
        {
          component: LOG_COMPONENTS.HTTP,
          method: 'GET',
          operation: LOG_OPERATIONS.ERROR,
        },
        `GET ${loggedPath} failed`,
        error as Error,
        { config: config as unknown },
      );
      // GRO-964 — honrar `skipSentryCapture` como ya lo hacen post() (y put()
      // desde el review de INT-1283). El `get` lo aceptaba en el tipo y lo
      // ignoraba, así que una consulta cuyo error es ESPERADO no tenía forma de
      // callarse: la sonda de capacidad del importador pega a un endpoint que en
      // producción todavía no existe, y su 404 —el estado normal hasta que salga
      // el backend async— entraba a Sentry en cada montaje del Directorio.
      // Ningún llamador de `get` pasaba el flag hasta ahora (los dos que existen
      // son POST), así que esto no cambia el comportamiento de nada preexistente.
      if (shouldCaptureFailure(error, config)) {
        sentry.captureException(error, {
          user: getUserInfo(),
          tags: { endpoint: normalizeRoute(pathname) },
          extra: {
            endpoint: pathname,
            axiosConfig: config,
          },
        });
      }
      return Promise.reject(error);
    }
  },

  put: async function <tRequest, tResponse>(
    pathname: string,
    request?: tRequest,
    config?: tAxiosCustomConfig,
  ): Promise<tResponse> {
    try {
      LLog.info(
        {
          component: LOG_COMPONENTS.HTTP,
          method: 'PUT',
          operation: LOG_OPERATIONS.API_CALL,
        },
        `Calling PUT ${pathname}`,
        { request: request as unknown, config: config as unknown },
      );
      const requestConfig: tAxiosCustomConfig = createRequestConfig(config);
      const response = await axiosInstance.put(
        pathname,
        request,
        requestConfig,
      );
      LLog.info(
        {
          component: LOG_COMPONENTS.HTTP,
          method: 'PUT',
          operation: LOG_OPERATIONS.RESPONSE,
        },
        `PUT ${pathname} success`,
        { status: response.status, data: response.data },
      );
      return Promise.resolve(response.data);
    } catch (error) {
      LLog.error(
        {
          component: LOG_COMPONENTS.HTTP,
          method: 'PUT',
          operation: LOG_OPERATIONS.ERROR,
        },
        `PUT ${pathname} failed`,
        error as Error,
        { request: request as unknown, config: config as unknown },
      );
      // INT-1283 review — honor skipSentryCapture like post() does; put was
      // the only verb capturing unconditionally, and it now carries the
      // highest-frequency, largest-payload call in the flows feature.
      if (shouldCaptureFailure(error, config)) {
        sentry.captureException(error, {
          user: getUserInfo(),
          tags: { endpoint: normalizeRoute(pathname) },
          extra: {
            endpoint: pathname,
            axiosConfig: config,
            request: request,
          },
        });
      }
      return Promise.reject(error);
    }
  },

  patchWithResponse,
  patch: async function <tRequest, tResponse>(
    pathname: string,
    request?: tRequest,
    config?: tAxiosCustomConfig,
  ): Promise<tResponse> {
    return (await patchWithResponse<tRequest, tResponse>(pathname, request, config)).data;
  },

  delete: async function <T>(
    pathname: string,
    config?: tAxiosCustomConfig,
  ): Promise<T> {
    try {
      LLog.info(
        {
          component: LOG_COMPONENTS.HTTP,
          method: 'DELETE',
          operation: LOG_OPERATIONS.API_CALL,
        },
        `Calling DELETE ${pathname}`,
        { config: config as unknown },
      );
      const requestConfig: tAxiosCustomConfig = createRequestConfig(config);
      const response = await axiosInstance.delete(pathname, requestConfig);
      LLog.info(
        {
          component: LOG_COMPONENTS.HTTP,
          method: 'DELETE',
          operation: LOG_OPERATIONS.RESPONSE,
        },
        `DELETE ${pathname} success`,
        { status: response.status, data: response.data },
      );
      return Promise.resolve(response.data);
    } catch (error) {
      LLog.error(
        {
          component: LOG_COMPONENTS.HTTP,
          method: 'DELETE',
          operation: LOG_OPERATIONS.ERROR,
        },
        `DELETE ${pathname} failed`,
        error as Error,
        { config: config as unknown },
      );
      sentry.captureException(error, {
        user: getUserInfo(),
        tags: { endpoint: normalizeRoute(pathname) },
        extra: {
          endpoint: pathname,
          axiosConfig: config,
        },
      });
      return Promise.reject(error);
    }
  },

  deleteBulkById: async function <tRequest, tResponse>(
    pathname: string,
    request?: tRequest,
    config?: tAxiosCustomConfig,
  ): Promise<tResponse> {
    try {
      const requestConfig: tAxiosCustomConfig = {
        ...createRequestConfig(config),
        ...(request ? { data: request } : {}),
      };
      const response = await axiosInstance.delete(pathname, requestConfig);

      return Promise.resolve(response.data);
    } catch (error) {
      sentry.captureException(error, {
        user: getUserInfo(),
        tags: { endpoint: normalizeRoute(pathname) },
        extra: {
          endpoint: pathname,
          axiosConfig: config,
        },
      });
      return Promise.reject(error);
    }
  },

  getById: async function <T>(
    pathname: string,
    id: string,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    try {
      const requestConfig: tAxiosCustomConfig = createRequestConfig(config);
      const response = await axiosInstance.get(
        `${pathname}/${id}`,
        requestConfig,
      );

      return Promise.resolve(response.data.data);
    } catch (error) {
      sentry.captureException(error, {
        user: getUserInfo(),
        tags: { endpoint: normalizeRoute(pathname) },
        extra: {
          endpoint: pathname,
          axiosConfig: config,
        },
      });
      return Promise.reject(error);
    }
  },
  patchById: async function <tRequest, tResponse>(
    pathname: string,
    id: string,
    request: tRequest,
    config?: tAxiosCustomConfig,
  ): Promise<tResponse> {
    try {
      const requestConfig: tAxiosCustomConfig = createRequestConfig(config);
      const response = await axiosInstance.patch(
        `${pathname}/${id}`,
        request,
        requestConfig,
      );
      return Promise.resolve(response.data);
    } catch (error) {
      sentry.captureException(error, {
        user: getUserInfo(),
        tags: { endpoint: normalizeRoute(pathname) },
        extra: {
          endpoint: pathname,
          axiosConfig: config,
          request: request,
        },
      });
      return Promise.reject(error);
    }
  },

  deleteById: async function <T>(
    pathname: string,
    id: string,
    config?: tAxiosCustomConfig,
  ): Promise<T> {
    try {
      const requestConfig: tAxiosCustomConfig = createRequestConfig(config);
      const response = await axiosInstance.delete(
        `${pathname}/${id}`,
        requestConfig,
      );

      return Promise.resolve(response.data);
    } catch (error) {
      sentry.captureException(error, {
        user: getUserInfo(),
        tags: { endpoint: normalizeRoute(pathname) },
        extra: {
          endpoint: pathname,
        },
      });
      return Promise.reject(error);
    }
  },
});
